<template>
  <div class="ca-file-test-page">
    <el-card class="test-card">
      <div slot="header" class="card-header">
        <span>CA 文件签章加密测试</span>
        <el-button size="small" @click="$router.go(-1)">返回</el-button>
      </div>

      <el-alert
        title="流程：先通过浏览器选择 PDF 并预览，再通过 CA 选择一个保存目录参考文件生成动态签章保存路径，签章成功返回后加密实际签章文件。"
        type="warning"
        :closable="false"
        show-icon />

      <el-form label-width="140px" class="test-form">
        <el-form-item label="选择 PDF">
          <el-upload
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            accept=".pdf"
            :on-change="handlePdfFileChange">
            <el-button type="primary" :loading="selecting">选择PDF文件</el-button>
          </el-upload>
          <div v-if="pdfFileName" class="form-tip">已选择：{{ pdfFileName }}</div>
        </el-form-item>

        <el-form-item label="签章保存路径">
          <el-input v-model="signSavePath" readonly>
            <el-button slot="append" :loading="pathSelecting" @click="selectSavePathByCA">选择保存目录</el-button>
          </el-input>
          <div class="form-tip">请选择目标目录下任意已有文件作为参考，系统会自动生成不重复的 sign_时间戳.pdf。</div>
        </el-form-item>

        <el-form-item label="加密输入 inFile">
          <el-input v-model="form.inFile" readonly />
        </el-form-item>

        <el-form-item label="密文输出 outFile">
          <el-input v-model="form.outFile" readonly />
          <div class="form-tip">注意：目录不存在会失败；加密输入使用签章成功返回的实际保存路径。</div>
        </el-form-item>

        <el-form-item label="对称密钥 sKey">
          <el-input v-model="form.sKey" placeholder="可点击生成随机密钥，或手动输入 Base64 编码密钥">
            <el-button slot="append" :loading="generating" @click="generateKey">生成16字节密钥</el-button>
          </el-input>
        </el-form-item>

        <el-form-item label="加密算法">
          <el-select v-model="form.algorithm" style="width: 260px">
            <el-option label="SM4_ECB（0x00000401）" :value="0x00000401" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="warning" :disabled="!pdfBase64" @click="goSign">签章</el-button>
          <el-button v-if="signed" type="primary" :loading="encrypting" @click="encryptFile">加密签章文件</el-button>
          <el-button @click="resetForm">清空</el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <div class="preview-block">
        <h3>PDF 预览（原文）</h3>
        <PdfViewer :pdf-url="pdfPreviewUrl" :show-toolbar="true" :download-file-name="pdfFileName || 'preview.pdf'" />
      </div>

      <el-divider />

      <div class="preview-block">
        <div class="signed-preview-header">
          <h3>PDF 预览（签章后）</h3>
          <el-upload
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            accept=".pdf"
            :on-change="handleSignedPdfFileChange">
            <el-button size="small" type="primary">选择签章后 PDF 预览</el-button>
          </el-upload>
          <el-button v-if="signedPdfPreviewUrl" size="small" @click="clearSignedPreview">清空</el-button>
        </div>
        <div v-if="signedPdfFileName" class="form-tip">已选择：{{ signedPdfFileName }}</div>
        <div v-if="!signedPdfPreviewUrl" class="form-tip">提示：签章保存到本地路径模式下，PDFClient 不会回传 base64。可手动选择磁盘上签章后的 PDF 文件进行预览。</div>
        <PdfViewer v-if="signedPdfPreviewUrl" :pdf-url="signedPdfPreviewUrl" :show-toolbar="true" :download-file-name="signedPdfFileName || 'signed.pdf'" />
      </div>

      <el-divider />

      <div class="result-block">
        <h3>调用结果</h3>
        <el-tag v-if="signed" type="success">已签章：{{ form.inFile }}</el-tag>
        <el-tag v-if="resultText" :type="resultSuccess ? 'success' : 'danger'">
          {{ resultText }}
        </el-tag>
        <div v-if="resultSuccess" class="locate-bar">
          <el-button type="success" size="small" @click="locateEncryptedFile">定位密文文件</el-button>
          <el-button size="small" @click="copyOutFilePath">复制密文路径</el-button>
          <span class="form-tip">如浏览器拦截 file:// 链接，请使用复制路径后到资源管理器粘贴。</span>
        </div>
        <pre class="log-panel">{{ logs.join('\n') }}</pre>
      </div>
    </el-card>
  </div>
</template>

<script>
import { XTXASynObject } from '@/api/ca/XTXASynObject'
import PdfViewer from '@/components/common/PdfViewer.vue'

const DEFAULT_SAVE_DIR = 'C:\\BJCAROOT\\pawdocs\\PDFClient'
const SIGN_STORAGE_KEY = 'caLocalSignPdfData'
const SIGN_RESULT_KEY = 'caLocalSignResult'

export default {
  name: 'CaFileTest',
  components: {
    PdfViewer
  },
  data() {
    return {
      xtxObject: new XTXASynObject(),
      generating: false,
      selecting: false,
      pathSelecting: false,
      encrypting: false,
      signed: false,
      saveDir: DEFAULT_SAVE_DIR,
      signSavePath: '',
      pdfFileName: '',
      pdfBase64: '',
      pdfPreviewUrl: '',
      signedPdfPreviewUrl: '',
      signedPdfFileName: '',
      resultText: '',
      resultSuccess: false,
      logs: [],
      form: {
        sKey: '',
        inFile: '',
        outFile: '',
        algorithm: 0x00000401
      }
    }
  },
  mounted() {
    this.refreshDynamicPaths()
    this.restorePdfData()
    this.checkSignResult()
  },
  activated() {
    this.restorePdfData()
    this.checkSignResult()
  },
  beforeDestroy() {
    if (this.pdfPreviewUrl) {
      URL.revokeObjectURL(this.pdfPreviewUrl)
    }
    if (this.signedPdfPreviewUrl) {
      URL.revokeObjectURL(this.signedPdfPreviewUrl)
    }
  },
  methods: {
    addLog(text) {
      this.logs.unshift(`[${new Date().toLocaleTimeString()}] ${text}`)
    },
    async ensureReady() {
      await this.xtxObject.initWebSocket()
    },
    buildTimestamp() {
      const pad = n => String(n).padStart(2, '0')
      const now = new Date()
      return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${now.getMilliseconds()}`
    },
    joinPath(dir, fileName) {
      return `${String(dir).replace(/[\\/]+$/, '')}\\${fileName}`
    },
    refreshDynamicPaths() {
      const signPath = this.joinPath(this.saveDir || DEFAULT_SAVE_DIR, `sign_${this.buildTimestamp()}.pdf`)
      this.signSavePath = signPath
      this.form.inFile = signPath
      this.form.outFile = signPath + '.enc'
      this.signed = false
    },
    async selectSavePathByCA() {
      this.pathSelecting = true
      try {
        await this.ensureReady()
        const filePath = await this.xtxObject.SelectFile()
        if (!filePath) {
          return
        }
        const index = Math.max(filePath.lastIndexOf('\\'), filePath.lastIndexOf('/'))
        this.saveDir = index >= 0 ? filePath.slice(0, index) : DEFAULT_SAVE_DIR
        this.refreshDynamicPaths()
        this.addLog('选择保存目录：' + this.saveDir)
      } catch (error) {
        this.addLog('选择保存目录异常：' + (error.message || error))
        this.$message.error('选择保存目录失败')
      } finally {
        this.pathSelecting = false
      }
    },
    handlePdfFileChange(file) {
      const rawFile = file && file.raw
      if (!rawFile) {
        this.$message.warning('文件不存在')
        return
      }
      if (rawFile.type && rawFile.type !== 'application/pdf') {
        this.$message.warning('请选择 PDF 文件')
        return
      }
      this.selecting = true
      if (this.pdfPreviewUrl) {
        URL.revokeObjectURL(this.pdfPreviewUrl)
      }
      this.pdfPreviewUrl = URL.createObjectURL(rawFile)
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result || ''
        this.pdfBase64 = String(result).includes(',') ? String(result).split(',').pop() : String(result)
        this.pdfFileName = rawFile.name || ''
        window.__caLocalSignPdfData = {
          fileName: this.pdfFileName,
          fileBase64: this.pdfBase64,
          savePath: this.signSavePath,
          returnPath: '/ca-file-test'
        }
        this.refreshDynamicPaths()
        sessionStorage.removeItem(SIGN_RESULT_KEY)
        this.addLog('选择 PDF：' + this.pdfFileName)
        this.selecting = false
      }
      reader.onerror = () => {
        this.selecting = false
        this.$message.error('读取 PDF 失败')
        this.addLog('读取 PDF 失败')
      }
      reader.readAsDataURL(rawFile)
    },
    goSign() {
      if (!this.pdfBase64) {
        this.$message.warning('请先选择 PDF 文件')
        return
      }
      window.__caLocalSignPdfData = {
        fileName: this.pdfFileName,
        fileBase64: this.pdfBase64,
        savePath: this.signSavePath,
        returnPath: '/ca-file-test'
      }
      sessionStorage.setItem(SIGN_STORAGE_KEY, JSON.stringify({
        fileName: this.pdfFileName,
        savePath: this.signSavePath,
        returnPath: '/ca-file-test',
        useMemory: true
      }))
      this.$router.push('/ca-local-sign-path-test')
    },
    restorePdfData() {
      if (this.pdfBase64) {
        return
      }
      const memoryData = window.__caLocalSignPdfData
      const dataText = sessionStorage.getItem(SIGN_STORAGE_KEY)
      if (!dataText && !memoryData) {
        return
      }
      try {
        const data = memoryData || JSON.parse(dataText)
        this.pdfFileName = data.fileName || ''
        this.pdfBase64 = data.fileBase64 || ''
        if (this.pdfBase64 && !this.pdfPreviewUrl) {
          const byteCharacters = atob(this.pdfBase64)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })
          this.pdfPreviewUrl = URL.createObjectURL(blob)
        }
      } catch (error) {
        this.addLog('恢复 PDF 数据失败：' + (error.message || error))
      }
    },
    checkSignResult() {
      const resultText = sessionStorage.getItem(SIGN_RESULT_KEY)
      if (!resultText) {
        return
      }
      try {
        const result = JSON.parse(resultText)
        this.signed = true
        this.form.inFile = result.savePath || this.signSavePath
        this.form.outFile = this.form.inFile + '.enc'
        this.addLog('签章完成，返回加密页：' + this.form.inFile)
        if (result.actualSavePath && result.actualSavePath !== this.form.inFile) {
          this.addLog('PDFClient 返回实际路径：' + result.actualSavePath)
        }
        const signedBase64 = window.__caLocalSignPdfData && window.__caLocalSignPdfData.signedBase64
        if (signedBase64) {
          this.setSignedPreviewFromBase64(signedBase64)
          this.signedPdfFileName = (result.savePath || '').split(/[\\/]/).pop() || 'signed.pdf'
          this.addLog('已加载签章后预览')
        }
      } catch (error) {
        this.signed = true
        this.form.inFile = this.signSavePath
        this.form.outFile = this.signSavePath + '.enc'
      }
      sessionStorage.removeItem(SIGN_RESULT_KEY)
    },
    async generateKey() {
      this.generating = true
      try {
        await this.ensureReady()
        await this.xtxObject.SOF_SetEncryptMethod(this.form.algorithm)
        const key = await this.xtxObject.SOF_GenRandom(16)
        this.form.sKey = key || ''
        this.addLog(key ? '生成对称密钥成功' : '生成对称密钥失败')
      } catch (error) {
        this.addLog('生成对称密钥异常：' + (error.message || error))
        this.$message.error('生成对称密钥失败')
      } finally {
        this.generating = false
      }
    },
    async encryptFile() {
      if (!this.signed) {
        this.$message.warning('请先完成签章')
        return
      }
      if (!this.form.sKey) {
        this.$message.warning('请先填写或生成对称密钥')
        return
      }
      if (!this.form.outFile) {
        this.$message.warning('请填写密文输出全路径')
        return
      }
      this.encrypting = true
      this.resultText = ''
      this.resultSuccess = false
      try {
        await this.ensureReady()
        const setResult = await this.xtxObject.SOF_SetEncryptMethod(this.form.algorithm)
        this.addLog('SOF_SetEncryptMethod 返回：' + setResult)
        const ret = await this.xtxObject.SOF_SymEncryptFile(this.form.sKey, this.form.inFile, this.form.outFile)
        this.resultSuccess = ret === true || ret === 'true' || ret === 'TRUE' || ret === 1 || ret === '1'
        this.resultText = this.resultSuccess ? '加密成功' : '加密失败'
        this.addLog(`SOF_SymEncryptFile 返回：${ret}`)
        this.addLog(`inFile=${this.form.inFile}`)
        this.addLog(`outFile=${this.form.outFile}`)
      } catch (error) {
        this.resultText = '调用异常'
        this.addLog('SOF_SymEncryptFile 异常：' + (error.message || error))
        this.$message.error('文件加密调用失败')
      } finally {
        this.encrypting = false
      }
    },
    resetForm() {
      this.form.sKey = ''
      this.refreshDynamicPaths()
      this.signed = false
      this.pdfFileName = ''
      this.pdfBase64 = ''
      if (this.pdfPreviewUrl) {
        URL.revokeObjectURL(this.pdfPreviewUrl)
      }
      this.pdfPreviewUrl = ''
      if (this.signedPdfPreviewUrl) {
        URL.revokeObjectURL(this.signedPdfPreviewUrl)
      }
      this.signedPdfPreviewUrl = ''
      this.signedPdfFileName = ''
      this.resultText = ''
      this.resultSuccess = false
      this.logs = []
      sessionStorage.removeItem(SIGN_STORAGE_KEY)
      sessionStorage.removeItem(SIGN_RESULT_KEY)
      delete window.__caLocalSignPdfData
    },
    getOutFolder() {
      const out = this.form.outFile || ''
      const idx = Math.max(out.lastIndexOf('\\'), out.lastIndexOf('/'))
      return idx >= 0 ? out.slice(0, idx) : ''
    },
    locateEncryptedFile() {
      const folder = this.getOutFolder()
      if (!folder) {
        this.$message.warning('未找到密文输出目录')
        return
      }
      const url = 'file:///' + folder.replace(/\\/g, '/')
      try {
        const opened = window.open(url, '_blank')
        if (!opened) {
          this.$message.info('浏览器拦截了 file:// 跳转，请点击复制密文路径手动打开')
        }
        this.addLog('尝试打开目录：' + folder)
      } catch (error) {
        this.addLog('打开目录异常：' + (error.message || error))
        this.$message.error('打开目录失败，请手动定位')
      }
    },
    setSignedPreviewFromBase64(base64) {
      if (this.signedPdfPreviewUrl) {
        URL.revokeObjectURL(this.signedPdfPreviewUrl)
      }
      const byteCharacters = atob(base64)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })
      this.signedPdfPreviewUrl = URL.createObjectURL(blob)
    },
    handleSignedPdfFileChange(file) {
      const rawFile = file && file.raw
      if (!rawFile) {
        return
      }
      if (rawFile.type && rawFile.type !== 'application/pdf') {
        this.$message.warning('请选择 PDF 文件')
        return
      }
      if (this.signedPdfPreviewUrl) {
        URL.revokeObjectURL(this.signedPdfPreviewUrl)
      }
      this.signedPdfPreviewUrl = URL.createObjectURL(rawFile)
      this.signedPdfFileName = rawFile.name || ''
      this.addLog('加载签章后 PDF 预览：' + this.signedPdfFileName)
    },
    clearSignedPreview() {
      if (this.signedPdfPreviewUrl) {
        URL.revokeObjectURL(this.signedPdfPreviewUrl)
      }
      this.signedPdfPreviewUrl = ''
      this.signedPdfFileName = ''
    },
    async copyOutFilePath() {
      const out = this.form.outFile || ''
      if (!out) {
        this.$message.warning('未找到密文输出路径')
        return
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(out)
        } else {
          const ta = document.createElement('textarea')
          ta.value = out
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$message.success('已复制密文路径')
      } catch (error) {
        this.addLog('复制密文路径异常：' + (error.message || error))
        this.$message.error('复制失败，请手动复制：' + out)
      }
    }
  }
}
</script>

<style scoped>
.ca-file-test-page {
  min-height: 100vh;
  padding: 24px;
  background: #f5f7fa;
}

.test-card {
  max-width: 1200px;
  margin: 0 auto;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
}

.test-form {
  margin-top: 24px;
}

.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #e6a23c;
}

.locate-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.preview-block h3,
.result-block h3 {
  margin: 0 0 12px;
  font-size: 16px;
}

.preview-block {
  height: 700px;
  margin-bottom: 16px;
}

.signed-preview-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.signed-preview-header h3 {
  margin: 0;
}

.log-panel {
  min-height: 180px;
  margin-top: 12px;
  padding: 12px;
  overflow: auto;
  color: #dcdfe6;
  background: #1f2d3d;
  border-radius: 6px;
  white-space: pre-wrap;
}
</style>
